import React, { useEffect, useState } from 'react';
import { usePersistedState } from '../helpers/useDashboardStorage';
import './MyInfoPage.css';

const TOOLTIP_EXPLANATIONS = {
  gpaWeighting: 'Unweighted usually means a 4.0 scale. Weighted includes extra points for harder classes. Other is useful if your school uses its own system.',
  apIb: 'Track AP, IB, or similarly advanced courses you are planning, taking now, or have completed.',
  psat: 'The PSAT is useful for practice and, in 11th grade, possible National Merit recognition.',
};

const ACTIVITY_TYPES = [
  'Academic', 'Art', 'Athletics', 'Career', 'Community Service', 'Cultural', 'Dance',
  'Debate', 'Environmental', 'Family Responsibility', 'Foreign Exchange', 'Journalism',
  'Junior ROTC', 'LGBT', 'Music', 'Religious', 'Research', 'Science', 'Social Justice',
  'Speech', 'Student Government', 'Technology', 'Theater', 'Work', 'Other'
];

const COURSE_STATUSES = ['Planning', 'Currently taking', 'Completed'];

const blankSatAttempt = { date: '', score: '', reading: '', math: '', notes: '' };
const blankActAttempt = { date: '', score: '', english: '', math: '', reading: '', science: '', notes: '' };
const blankPsatAttempt = { date: '', score: '', notes: '' };
const blankApCourse = { name: '', status: 'Planning', score: '' };
const blankDualCourse = { name: '', provider: '', status: 'Planning', grade: '' };

function InfoTooltip({ fieldName }) {
  const [showTooltip, setShowTooltip] = useState(false);
  const explanation = TOOLTIP_EXPLANATIONS[fieldName];

  if (!explanation) return null;

  return (
    <div className="info-tooltip-wrapper">
      <button
        className="info-tooltip-btn"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onClick={() => setShowTooltip(!showTooltip)}
        type="button"
        aria-label="More information"
      >
        i
      </button>
      {showTooltip && <div className="info-tooltip-box">{explanation}</div>}
    </div>
  );
}

function GradeCheckboxes({ selectedGrades = [], onChange }) {
  const grades = [9, 10, 11, 12];
  return (
    <div className="my-info-grade-checkboxes">
      {grades.map((grade) => (
        <label key={grade} className="my-info-grade-checkbox">
          <input
            type="checkbox"
            checked={selectedGrades.includes(grade)}
            onChange={(e) => {
              if (e.target.checked) {
                onChange([...selectedGrades, grade].sort());
              } else {
                onChange(selectedGrades.filter((g) => g !== grade));
              }
            }}
          />
          {grade}
        </label>
      ))}
    </div>
  );
}

function toNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function hasValue(value) {
  return value !== null && value !== undefined && String(value).trim() !== '';
}

function getSatReading(score) {
  return toNumber(score?.reading ?? score?.readingWriting);
}

function getSatMath(score) {
  return toNumber(score?.math);
}

function getSatTotal(score) {
  const reading = getSatReading(score);
  const math = getSatMath(score);
  if (reading !== null && math !== null) return reading + math;
  return toNumber(score?.score);
}

function getSatSuperscore(scores = []) {
  const bestReading = Math.max(...scores.map(getSatReading).filter((score) => score !== null), -Infinity);
  const bestMath = Math.max(...scores.map(getSatMath).filter((score) => score !== null), -Infinity);
  const bestTotal = Math.max(...scores.map(getSatTotal).filter((score) => score !== null), -Infinity);

  if (bestReading !== -Infinity && bestMath !== -Infinity) {
    return { total: bestReading + bestMath, reading: bestReading, math: bestMath };
  }

  return {
    total: bestTotal === -Infinity ? null : bestTotal,
    reading: bestReading === -Infinity ? null : bestReading,
    math: bestMath === -Infinity ? null : bestMath,
  };
}

function getActComposite(score) {
  const sections = ['english', 'math', 'reading', 'science'].map((field) => toNumber(score?.[field]));
  if (sections.every((section) => section !== null)) {
    return Math.round(sections.reduce((sum, section) => sum + section, 0) / sections.length);
  }
  return toNumber(score?.score);
}

function getActSuperscore(scores = []) {
  const bestSections = ['english', 'math', 'reading', 'science'].reduce((acc, field) => {
    const best = Math.max(...scores.map((score) => toNumber(score?.[field])).filter((score) => score !== null), -Infinity);
    acc[field] = best === -Infinity ? null : best;
    return acc;
  }, {});

  const hasAllSections = Object.values(bestSections).every((score) => score !== null);
  const bestComposite = Math.max(...scores.map(getActComposite).filter((score) => score !== null), -Infinity);

  return {
    ...bestSections,
    composite: hasAllSections
      ? Math.round(Object.values(bestSections).reduce((sum, score) => sum + score, 0) / 4)
      : bestComposite === -Infinity ? null : bestComposite,
  };
}

function getBestPsat(scores = []) {
  const best = Math.max(...scores.map((score) => toNumber(score?.score)).filter((score) => score !== null), -Infinity);
  return best === -Infinity ? null : best;
}

function formatScore(score, fallback = '--') {
  return score === null || score === undefined || score === '' ? fallback : score;
}

function formatGpaType(type) {
  if (type === 'weighted') return 'Weighted';
  if (type === 'other') return 'Other';
  return 'Unweighted';
}

function normalizeApCourse(course = {}) {
  return {
    ...blankApCourse,
    ...course,
    status: course.status || (hasValue(course.score) ? 'Completed' : 'Planning'),
  };
}

function normalizeDualCourse(course = {}) {
  return {
    ...blankDualCourse,
    ...course,
    status: course.status || (hasValue(course.grade) ? 'Completed' : 'Planning'),
  };
}

function MyInfoPage() {
  const [personalInfo, setPersonalInfo] = usePersistedState('my_info_personal', {
    highSchoolName: '',
    ceebCode: '',
    graduationYear: '',
    graduationYearConfirmed: false,
    homeCity: '',
    stateOfResidence: '',
    firstGeneration: false,
    citizenship: 'US Citizen',
    classRank: '',
    classSize: '',
  });

  const [academicProfile, setAcademicProfile] = usePersistedState('my_info_academic', {
    gpa: '',
    gpaGoal: '',
    gpaWeighting: 'unweighted',
    apIbCourses: [blankApCourse],
    dualEnrollment: [blankDualCourse],
    intendedMajor: '',
  });

  const [testScores, setTestScores] = usePersistedState('my_info_testscores', {
    satGoal: '',
    actGoal: '',
    psatGoal: '',
    satScores: [blankSatAttempt],
    practiceSatScores: [blankSatAttempt],
    actScores: [blankActAttempt],
    practiceActScores: [blankActAttempt],
    psatScores: [blankPsatAttempt],
    practicePsatScores: [blankPsatAttempt],
    psat: { date: '', score: '', nationalMerit: false },
    satSubjectTests: [{ name: '', score: '' }],
    toeflIelts: { type: '', score: '' },
  });

  const [extracurriculars, setExtracurriculars] = usePersistedState('my_info_extracurriculars', [
    { type: '', position: '', organization: '', description: '', hoursPerWeek: '', weeksPerYear: '', grades: [] },
  ]);

  const [awards, setAwards] = usePersistedState('my_info_awards', [
    { title: '', level: 'school', grades: [] },
  ]);

  const [activeSection, setActiveSection] = useState('main');

  useEffect(() => {
    const hasLegacyPsat = hasValue(testScores.psat?.score) || hasValue(testScores.psat?.date);
    const hasNewPsat = Array.isArray(testScores.psatScores) && testScores.psatScores.some((score) => hasValue(score.score) || hasValue(score.date));

    if (hasLegacyPsat && !hasNewPsat) {
      setTestScores((prev) => ({
        ...prev,
        psatScores: [{
          date: prev.psat?.date || '',
          score: prev.psat?.score || '',
          notes: prev.psat?.nationalMerit ? 'National Merit recognized' : '',
        }],
      }));
    }
  }, [setTestScores, testScores.psat, testScores.psatScores]);

  const satScores = Array.isArray(testScores.satScores) ? testScores.satScores : [blankSatAttempt];
  const practiceSatScores = Array.isArray(testScores.practiceSatScores) ? testScores.practiceSatScores : [blankSatAttempt];
  const actScores = Array.isArray(testScores.actScores) ? testScores.actScores : [blankActAttempt];
  const practiceActScores = Array.isArray(testScores.practiceActScores) ? testScores.practiceActScores : [blankActAttempt];
  const psatScores = Array.isArray(testScores.psatScores) ? testScores.psatScores : [blankPsatAttempt];
  const practicePsatScores = Array.isArray(testScores.practicePsatScores) ? testScores.practicePsatScores : [blankPsatAttempt];
  const apIbCourses = Array.isArray(academicProfile.apIbCourses) ? academicProfile.apIbCourses.map(normalizeApCourse) : [blankApCourse];
  const dualEnrollment = Array.isArray(academicProfile.dualEnrollment) ? academicProfile.dualEnrollment.map(normalizeDualCourse) : [blankDualCourse];

  const officialSat = getSatSuperscore(satScores);
  const practiceSat = getSatSuperscore(practiceSatScores);
  const officialAct = getActSuperscore(actScores);
  const practiceAct = getActSuperscore(practiceActScores);
  const officialPsat = getBestPsat(psatScores);
  const practicePsat = getBestPsat(practicePsatScores);
  const locationSummary = [personalInfo.homeCity, personalInfo.stateOfResidence].filter(Boolean).join(', ');
  const visibleAwards = awards.filter((award) => hasValue(award.title));
  const visibleActivities = extracurriculars.filter((activity) => (
    hasValue(activity.type)
    || hasValue(activity.position)
    || hasValue(activity.organization)
    || hasValue(activity.description)
    || hasValue(activity.hoursPerWeek)
    || hasValue(activity.weeksPerYear)
    || (Array.isArray(activity.grades) && activity.grades.length > 0)
  ));

  const handlePersonalChange = (field, value) => {
    setPersonalInfo((prev) => ({ ...prev, [field]: value }));
  };

  const handleGraduationYearChange = (event) => {
    const value = event.target.value;
    setPersonalInfo((prev) => ({
      ...prev,
      graduationYear: value ? Number(value) : '',
      graduationYearConfirmed: Boolean(value),
    }));
  };

  const handleAcademicChange = (field, value) => {
    setAcademicProfile((prev) => ({ ...prev, [field]: value }));
  };

  const updateAcademicArray = (arrayKey, index, field, value) => {
    setAcademicProfile((prev) => {
      const items = Array.isArray(prev[arrayKey]) ? [...prev[arrayKey]] : [];
      items[index] = { ...(items[index] || {}), [field]: value };
      return { ...prev, [arrayKey]: items };
    });
  };

  const addAcademicItem = (arrayKey, blankItem) => {
    setAcademicProfile((prev) => ({
      ...prev,
      [arrayKey]: [...(Array.isArray(prev[arrayKey]) ? prev[arrayKey] : []), blankItem],
    }));
  };

  const removeAcademicItem = (arrayKey, index, blankItem) => {
    setAcademicProfile((prev) => {
      const remaining = (Array.isArray(prev[arrayKey]) ? prev[arrayKey] : []).filter((_, i) => i !== index);
      return { ...prev, [arrayKey]: remaining.length ? remaining : [blankItem] };
    });
  };

  const handleTestGoalChange = (field, value) => {
    setTestScores((prev) => ({ ...prev, [field]: value }));
  };

  const updateTestArray = (arrayKey, index, field, value) => {
    setTestScores((prev) => {
      const items = Array.isArray(prev[arrayKey]) ? [...prev[arrayKey]] : [];
      items[index] = { ...(items[index] || {}), [field]: value };
      return { ...prev, [arrayKey]: items };
    });
  };

  const addTestAttempt = (arrayKey, blankAttempt) => {
    setTestScores((prev) => ({
      ...prev,
      [arrayKey]: [...(Array.isArray(prev[arrayKey]) ? prev[arrayKey] : []), blankAttempt],
    }));
  };

  const removeTestAttempt = (arrayKey, index, blankAttempt) => {
    setTestScores((prev) => {
      const remaining = (Array.isArray(prev[arrayKey]) ? prev[arrayKey] : []).filter((_, i) => i !== index);
      return { ...prev, [arrayKey]: remaining.length ? remaining : [blankAttempt] };
    });
  };

  const handleAddExtracurricular = () => {
    if (extracurriculars.length < 10) {
      setExtracurriculars((prev) => [
        ...prev,
        { type: '', position: '', organization: '', description: '', hoursPerWeek: '', weeksPerYear: '', grades: [] },
      ]);
    }
  };

  const handleRemoveExtracurricular = (index) => {
    setExtracurriculars((prev) => prev.filter((_, i) => i !== index));
  };

  const handleExtracurricularChange = (index, field, value) => {
    setExtracurriculars((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleExtracurricularGradesChange = (index, grades) => {
    setExtracurriculars((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], grades };
      return updated;
    });
  };

  const handleAddAward = () => {
    if (awards.length < 5) {
      setAwards((prev) => [...prev, { title: '', level: 'school', grades: [] }]);
    }
  };

  const handleRemoveAward = (index) => {
    setAwards((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAwardChange = (index, field, value) => {
    setAwards((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleAwardGradesChange = (index, grades) => {
    setAwards((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], grades };
      return updated;
    });
  };

  const renderSatAttempts = (title, arrayKey, scores, isPractice = false) => (
    <div className="my-info-score-group">
      <div className="my-info-score-group__header">
        <div>
          <h4 className="my-info-score-group__title">{title}</h4>
          <p className="my-info-score-group__subtitle">
            {isPractice ? 'Practice attempts stay separate from your official summary.' : 'These attempts power the SAT snapshot card.'}
          </p>
        </div>
        <button className="my-info-add-btn" onClick={() => addTestAttempt(arrayKey, blankSatAttempt)} type="button">
          + Add Attempt
        </button>
      </div>

      {scores.map((score, index) => (
        <div key={`${arrayKey}-${index}`} className="my-info-score-entry my-info-score-entry--compact">
          <div className="my-info-grid my-info-grid--4col">
            <div className="my-info-field">
              <label className="my-info-label">Date</label>
              <input className="my-info-input" type="date" value={score.date || ''} onChange={(e) => updateTestArray(arrayKey, index, 'date', e.target.value)} />
            </div>
            <div className="my-info-field">
              <label className="my-info-label">Reading/Writing</label>
              <input className="my-info-input" type="number" value={score.reading || score.readingWriting || ''} onChange={(e) => updateTestArray(arrayKey, index, 'reading', e.target.value)} placeholder="700" min="200" max="800" />
            </div>
            <div className="my-info-field">
              <label className="my-info-label">Math</label>
              <input className="my-info-input" type="number" value={score.math || ''} onChange={(e) => updateTestArray(arrayKey, index, 'math', e.target.value)} placeholder="720" min="200" max="800" />
            </div>
            <div className="my-info-field">
              <label className="my-info-label">Total</label>
              <div className="my-info-score-total">{formatScore(getSatTotal(score))}</div>
            </div>
          </div>
          <button className="my-info-remove-btn" onClick={() => removeTestAttempt(arrayKey, index, blankSatAttempt)} type="button">
            Remove
          </button>
        </div>
      ))}
    </div>
  );

  const renderActAttempts = (title, arrayKey, scores, isPractice = false) => (
    <div className="my-info-score-group">
      <div className="my-info-score-group__header">
        <div>
          <h4 className="my-info-score-group__title">{title}</h4>
          <p className="my-info-score-group__subtitle">
            {isPractice ? 'Practice attempts help you track progress without changing your official composite.' : 'These attempts power the ACT snapshot card.'}
          </p>
        </div>
        <button className="my-info-add-btn" onClick={() => addTestAttempt(arrayKey, blankActAttempt)} type="button">
          + Add Attempt
        </button>
      </div>

      {scores.map((score, index) => (
        <div key={`${arrayKey}-${index}`} className="my-info-score-entry my-info-score-entry--compact">
          <div className="my-info-grid my-info-grid--3col">
            <div className="my-info-field">
              <label className="my-info-label">Date</label>
              <input className="my-info-input" type="date" value={score.date || ''} onChange={(e) => updateTestArray(arrayKey, index, 'date', e.target.value)} />
            </div>
            <div className="my-info-field">
              <label className="my-info-label">Composite</label>
              <div className="my-info-score-total">{formatScore(getActComposite(score))}</div>
            </div>
            <div className="my-info-field">
              <label className="my-info-label">Optional saved composite</label>
              <input className="my-info-input" type="number" value={score.score || ''} onChange={(e) => updateTestArray(arrayKey, index, 'score', e.target.value)} placeholder="31" min="1" max="36" />
            </div>
          </div>
          <div className="my-info-grid my-info-grid--4col">
            {['english', 'math', 'reading', 'science'].map((field) => (
              <div className="my-info-field" key={field}>
                <label className="my-info-label">{field.charAt(0).toUpperCase() + field.slice(1)}</label>
                <input className="my-info-input" type="number" value={score[field] || ''} onChange={(e) => updateTestArray(arrayKey, index, field, e.target.value)} placeholder="30" min="1" max="36" />
              </div>
            ))}
          </div>
          <button className="my-info-remove-btn" onClick={() => removeTestAttempt(arrayKey, index, blankActAttempt)} type="button">
            Remove
          </button>
        </div>
      ))}
    </div>
  );

  const renderPsatAttempts = (title, arrayKey, scores, isPractice = false) => (
    <div className="my-info-score-group">
      <div className="my-info-score-group__header">
        <div>
          <h4 className="my-info-score-group__title">{title}</h4>
          <p className="my-info-score-group__subtitle">
            {isPractice ? 'Practice PSAT scores are tracked separately.' : 'Official PSAT attempts power the PSAT snapshot card.'}
          </p>
        </div>
        <button className="my-info-add-btn" onClick={() => addTestAttempt(arrayKey, blankPsatAttempt)} type="button">
          + Add Attempt
        </button>
      </div>

      {scores.map((score, index) => (
        <div key={`${arrayKey}-${index}`} className="my-info-score-entry my-info-score-entry--compact">
          <div className="my-info-grid my-info-grid--3col">
            <div className="my-info-field">
              <label className="my-info-label">Date</label>
              <input className="my-info-input" type="date" value={score.date || ''} onChange={(e) => updateTestArray(arrayKey, index, 'date', e.target.value)} />
            </div>
            <div className="my-info-field">
              <label className="my-info-label">Score</label>
              <input className="my-info-input" type="number" value={score.score || ''} onChange={(e) => updateTestArray(arrayKey, index, 'score', e.target.value)} placeholder="1280" min="320" max="1520" />
            </div>
            <div className="my-info-field">
              <label className="my-info-label">Notes / status</label>
              <input className="my-info-input" type="text" value={score.notes || ''} onChange={(e) => updateTestArray(arrayKey, index, 'notes', e.target.value)} placeholder="National Merit, practice set, etc." />
            </div>
          </div>
          <button className="my-info-remove-btn" onClick={() => removeTestAttempt(arrayKey, index, blankPsatAttempt)} type="button">
            Remove
          </button>
        </div>
      ))}
    </div>
  );

  const renderTestingGoals = () => (
    <section className="my-info-goals-panel">
      <div className="my-info-section__header">
        <div>
          <h2 className="my-info-section__title">Testing Goals</h2>
          <p className="my-info-section__subtitle">Keep target scores beside your current official bests.</p>
        </div>
      </div>

      <div className="my-info-goals-grid my-info-goals-grid--3">
        <article className="my-info-goal-card">
          <div>
            <span className="my-info-goal-card__label">SAT</span>
            <strong className="my-info-goal-card__current">{formatScore(officialSat.total)}</strong>
            <span className="my-info-goal-card__meta">RW {formatScore(officialSat.reading)} + Math {formatScore(officialSat.math)}</span>
          </div>
          <label className="my-info-goal-card__field">
            <span>Goal</span>
            <input type="number" value={testScores.satGoal || ''} onChange={(e) => handleTestGoalChange('satGoal', e.target.value)} placeholder="1450" min="400" max="1600" />
          </label>
        </article>

        <article className="my-info-goal-card">
          <div>
            <span className="my-info-goal-card__label">ACT</span>
            <strong className="my-info-goal-card__current">{formatScore(officialAct.composite)}</strong>
            <span className="my-info-goal-card__meta">E {formatScore(officialAct.english)} M {formatScore(officialAct.math)} R {formatScore(officialAct.reading)} S {formatScore(officialAct.science)}</span>
          </div>
          <label className="my-info-goal-card__field">
            <span>Goal</span>
            <input type="number" value={testScores.actGoal || ''} onChange={(e) => handleTestGoalChange('actGoal', e.target.value)} placeholder="32" min="1" max="36" />
          </label>
        </article>

        <article className="my-info-goal-card">
          <div>
            <span className="my-info-goal-card__label">PSAT</span>
            <strong className="my-info-goal-card__current">{formatScore(officialPsat)}</strong>
            <span className="my-info-goal-card__meta">Practice best {formatScore(practicePsat)}</span>
          </div>
          <label className="my-info-goal-card__field">
            <span>Goal</span>
            <input type="number" value={testScores.psatGoal || ''} onChange={(e) => handleTestGoalChange('psatGoal', e.target.value)} placeholder="1350" min="320" max="1520" />
          </label>
        </article>
      </div>
    </section>
  );

  const renderSnapshot = () => (
    <section className="my-info-snapshot-grid" aria-label="Academic snapshot">
      <article className="my-info-snapshot-card">
        <span className="my-info-score-badge">GPA</span>
        <strong className="my-info-snapshot-card__value">{formatScore(academicProfile.gpa)}</strong>
        <span className="my-info-snapshot-card__meta">{formatGpaType(academicProfile.gpaWeighting)}</span>
        <span className="my-info-snapshot-card__detail">Current GPA</span>
      </article>

      <article className="my-info-snapshot-card">
        <span className="my-info-score-badge">SAT</span>
        <strong className="my-info-snapshot-card__value">{formatScore(officialSat.total)}</strong>
        <span className="my-info-snapshot-card__meta">RW {formatScore(officialSat.reading)} + Math {formatScore(officialSat.math)}</span>
        <span className="my-info-snapshot-card__detail">Official superscore</span>
      </article>

      <article className="my-info-snapshot-card">
        <span className="my-info-score-badge">ACT</span>
        <strong className="my-info-snapshot-card__value">{formatScore(officialAct.composite)}</strong>
        <span className="my-info-snapshot-card__meta">E {formatScore(officialAct.english)} M {formatScore(officialAct.math)} R {formatScore(officialAct.reading)} S {formatScore(officialAct.science)}</span>
        <span className="my-info-snapshot-card__detail">Official superscore</span>
      </article>

      <article className="my-info-snapshot-card">
        <span className="my-info-score-badge">PSAT</span>
        <strong className="my-info-snapshot-card__value">{formatScore(officialPsat)}</strong>
        <span className="my-info-snapshot-card__meta">Practice best {formatScore(practicePsat)}</span>
        <span className="my-info-snapshot-card__detail">Official best</span>
      </article>

      <article className="my-info-snapshot-card my-info-snapshot-card--full">
        <span className="my-info-score-badge">Profile</span>
        <strong className="my-info-snapshot-card__value my-info-snapshot-card__value--text">
          {academicProfile.intendedMajor || 'Intended major'}
        </strong>
        <span className="my-info-snapshot-card__meta">{locationSummary || 'Home city and state'}</span>
        <span className="my-info-snapshot-card__detail">
          {[personalInfo.highSchoolName, personalInfo.graduationYear].filter(Boolean).join(' - ') || 'High school context'}
        </span>
      </article>
    </section>
  );

  const renderProfileBasics = () => (
    <section className="my-info-section">
      <div className="my-info-section__header">
        <div>
          <h2 className="my-info-section__title">Profile Basics</h2>
          <p className="my-info-section__subtitle">Keep the personal context that shapes your college list here.</p>
        </div>
      </div>

      <div className="my-info-grid my-info-grid--2col">
        <div className="my-info-field">
          <label className="my-info-label">Intended Major / Area of Interest</label>
          <input className="my-info-input" type="text" value={academicProfile.intendedMajor || ''} onChange={(e) => handleAcademicChange('intendedMajor', e.target.value)} placeholder="Computer Science, Biology, Undeclared" />
        </div>
        <div className="my-info-field">
          <label className="my-info-label">Home City</label>
          <input className="my-info-input" type="text" value={personalInfo.homeCity || ''} onChange={(e) => handlePersonalChange('homeCity', e.target.value)} placeholder="Los Angeles" />
        </div>
      </div>

      <div className="my-info-grid my-info-grid--2col">
        <div className="my-info-field">
          <label className="my-info-label">State</label>
          <input className="my-info-input" type="text" value={personalInfo.stateOfResidence || ''} onChange={(e) => handlePersonalChange('stateOfResidence', e.target.value)} placeholder="California" />
        </div>
        <div className="my-info-field">
          <label className="my-info-label">High School</label>
          <input className="my-info-input" type="text" value={personalInfo.highSchoolName || ''} onChange={(e) => handlePersonalChange('highSchoolName', e.target.value)} placeholder="Lincoln High School" />
        </div>
      </div>
    </section>
  );

  const renderProfilePreview = () => (
    <section className="my-info-section">
      <div className="my-info-section__header">
        <div>
          <span className="my-info-prep-label">Application Prep</span>
          <h2 className="my-info-section__title">Extracurriculars & Awards Preview</h2>
          <p className="my-info-section__subtitle">A quick read of what is saved for future applications. Edit the full lists in Extracurriculars & Awards.</p>
        </div>
        <div className="my-info-section__count">{visibleActivities.length} activities · {visibleAwards.length} awards</div>
      </div>

      <div className="my-info-preview-grid">
        <div className="my-info-preview-list">
          <h3 className="my-info-subsection__title">Extracurriculars</h3>
          {visibleActivities.length ? (
            visibleActivities.slice(0, 3).map((activity, index) => (
              <div className="my-info-preview-item" key={`activity-preview-${index}`}>
                <strong>{activity.organization || activity.type || 'Activity'}</strong>
                <span>{activity.position || activity.type || 'Saved activity'}</span>
              </div>
            ))
          ) : (
            <p className="my-info-empty-text">No activities added yet.</p>
          )}
        </div>

        <div className="my-info-preview-list">
          <h3 className="my-info-subsection__title">Awards</h3>
          {visibleAwards.length ? (
            visibleAwards.slice(0, 3).map((award, index) => (
              <div className="my-info-preview-item" key={`award-preview-${index}`}>
                <strong>{award.title}</strong>
                <span>{award.level || 'Saved award'}</span>
              </div>
            ))
          ) : (
            <p className="my-info-empty-text">No awards added yet.</p>
          )}
        </div>
      </div>
    </section>
  );

  return (
    <div className="my-info-page">
      <div className="my-info-page__header">
        <h1 className="my-info-page__title">My Info</h1>
        <p className="my-info-page__subtitle">
          Track your academic goals, best scores, coursework, extracurriculars, and awards in one place.
        </p>
      </div>

      <div className="my-info-tabs" role="tablist" aria-label="My Info sections">
        <button
          className={`my-info-tab ${activeSection === 'main' ? 'my-info-tab--active' : ''}`}
          onClick={() => setActiveSection('main')}
          type="button"
        >
          Main
        </button>
        <button
          className={`my-info-tab ${activeSection === 'academic' ? 'my-info-tab--active' : ''}`}
          onClick={() => setActiveSection('academic')}
          type="button"
        >
          Academics
        </button>
        <button
          className={`my-info-tab ${activeSection === 'testing' ? 'my-info-tab--active' : ''}`}
          onClick={() => setActiveSection('testing')}
          type="button"
        >
          Testing
        </button>
        <button
          className={`my-info-tab ${activeSection === 'honors' ? 'my-info-tab--active' : ''}`}
          onClick={() => setActiveSection('honors')}
          type="button"
        >
          Extracurriculars & Awards
        </button>
      </div>

      {activeSection === 'main' && (
        <div className="my-info-tab-panel">
          {renderSnapshot()}
          {renderProfilePreview()}
          {renderProfileBasics()}
        </div>
      )}

      {activeSection === 'academic' && (
        <div className="my-info-tab-panel">
      <section className="my-info-section">
        <div className="my-info-section__header">
          <div>
            <h2 className="my-info-section__title">Academic Basics</h2>
            <p className="my-info-section__subtitle">Keep GPA and school-year details close to your score tracker.</p>
          </div>
        </div>

        <div className="my-info-grid my-info-grid--3col">
          <div className="my-info-field">
            <label className="my-info-label">Current GPA</label>
            <input className="my-info-input" type="number" value={academicProfile.gpa || ''} onChange={(e) => handleAcademicChange('gpa', e.target.value)} placeholder="3.8" step="0.01" min="0" max="6" />
          </div>
          <div className="my-info-field">
            <label className="my-info-label">GPA Type <InfoTooltip fieldName="gpaWeighting" /></label>
            <select className="my-info-input" value={academicProfile.gpaWeighting || 'unweighted'} onChange={(e) => handleAcademicChange('gpaWeighting', e.target.value)}>
              <option value="unweighted">Unweighted</option>
              <option value="weighted">Weighted</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="my-info-field">
            <label className="my-info-label">GPA Goal</label>
            <input className="my-info-input" type="number" value={academicProfile.gpaGoal || ''} onChange={(e) => handleAcademicChange('gpaGoal', e.target.value)} placeholder="4.0" min="0" max="6" step="0.01" />
          </div>
        </div>

        <div className="my-info-grid my-info-grid--2col">
          <div className="my-info-field">
            <label className="my-info-label">High School</label>
            <input className="my-info-input" type="text" value={personalInfo.highSchoolName || ''} onChange={(e) => handlePersonalChange('highSchoolName', e.target.value)} placeholder="Lincoln High School" />
          </div>
          <div className="my-info-field">
            <label className="my-info-label">Graduation Year</label>
            <select
              className="my-info-input"
              value={personalInfo.graduationYearConfirmed ? personalInfo.graduationYear : ''}
              onChange={handleGraduationYearChange}
            >
              <option value="">Select graduation year</option>
              {[...Array(13)].map((_, i) => {
                const year = new Date().getFullYear() - 5 + i;
                return <option key={year} value={year}>{year}</option>;
              })}
            </select>
            <span className="my-info-field__hint">MaiColleges waits to suggest checklist dates and reminders until you choose a year.</span>
          </div>
        </div>
      </section>
      <section className="my-info-section">
        <div className="my-info-section__header">
          <div>
            <h2 className="my-info-section__title">Courses</h2>
            <p className="my-info-section__subtitle">Plan, track, and record advanced coursework.</p>
          </div>
        </div>

        <div className="my-info-subsection">
          <div className="my-info-subsection__header">
            <h3 className="my-info-subsection__title">AP / IB Courses <InfoTooltip fieldName="apIb" /></h3>
            <button className="my-info-add-btn" onClick={() => addAcademicItem('apIbCourses', blankApCourse)} type="button">+ Add Course</button>
          </div>

          {apIbCourses.map((course, index) => (
            <div key={`ap-${index}`} className="my-info-score-entry">
              <div className="my-info-grid my-info-grid--3col">
                <div className="my-info-field">
                  <label className="my-info-label">Course Name</label>
                  <input className="my-info-input" type="text" value={course.name || ''} onChange={(e) => updateAcademicArray('apIbCourses', index, 'name', e.target.value)} placeholder="AP Biology" />
                </div>
                <div className="my-info-field">
                  <label className="my-info-label">Status</label>
                  <select className="my-info-input" value={course.status || 'Planning'} onChange={(e) => updateAcademicArray('apIbCourses', index, 'status', e.target.value)}>
                    {COURSE_STATUSES.map((status) => <option key={status} value={status}>{status}</option>)}
                  </select>
                </div>
                <div className="my-info-field">
                  <label className="my-info-label">Score</label>
                  <input className="my-info-input" type="text" value={course.score || ''} onChange={(e) => updateAcademicArray('apIbCourses', index, 'score', e.target.value)} placeholder="5, predicted, waiting" />
                </div>
              </div>
              <button className="my-info-remove-btn" onClick={() => removeAcademicItem('apIbCourses', index, blankApCourse)} type="button">Remove</button>
            </div>
          ))}
        </div>

        <div className="my-info-subsection">
          <div className="my-info-subsection__header">
            <h3 className="my-info-subsection__title">Dual Enrollment</h3>
            <button className="my-info-add-btn" onClick={() => addAcademicItem('dualEnrollment', blankDualCourse)} type="button">+ Add Course</button>
          </div>

          {dualEnrollment.map((course, index) => (
            <div key={`dual-${index}`} className="my-info-score-entry">
              <div className="my-info-grid my-info-grid--4col">
                <div className="my-info-field">
                  <label className="my-info-label">Course Name</label>
                  <input className="my-info-input" type="text" value={course.name || ''} onChange={(e) => updateAcademicArray('dualEnrollment', index, 'name', e.target.value)} placeholder="College Statistics" />
                </div>
                <div className="my-info-field">
                  <label className="my-info-label">School / Provider</label>
                  <input className="my-info-input" type="text" value={course.provider || ''} onChange={(e) => updateAcademicArray('dualEnrollment', index, 'provider', e.target.value)} placeholder="Community college" />
                </div>
                <div className="my-info-field">
                  <label className="my-info-label">Status</label>
                  <select className="my-info-input" value={course.status || 'Planning'} onChange={(e) => updateAcademicArray('dualEnrollment', index, 'status', e.target.value)}>
                    {COURSE_STATUSES.map((status) => <option key={status} value={status}>{status}</option>)}
                  </select>
                </div>
                <div className="my-info-field">
                  <label className="my-info-label">Grade / Score</label>
                  <input className="my-info-input" type="text" value={course.grade || ''} onChange={(e) => updateAcademicArray('dualEnrollment', index, 'grade', e.target.value)} placeholder="A, 96, in progress" />
                </div>
              </div>
              <button className="my-info-remove-btn" onClick={() => removeAcademicItem('dualEnrollment', index, blankDualCourse)} type="button">Remove</button>
            </div>
          ))}
        </div>
      </section>
        </div>
      )}

      {activeSection === 'testing' && (
        <div className="my-info-tab-panel">
          {renderTestingGoals()}

      <section className="my-info-section">
        <div className="my-info-section__header">
          <div>
            <h2 className="my-info-section__title">Test Score Tracker</h2>
            <p className="my-info-section__subtitle">Official scores build your snapshot. Practice scores show progress without changing it.</p>
          </div>
        </div>

        <div className="my-info-test-family">
          <div className="my-info-test-family__header">
            <h3 className="my-info-subsection__title">SAT</h3>
            <div className="my-info-score-summary">
              <span className="my-info-score-badge">Official superscore {formatScore(officialSat.total)}</span>
              <span className="my-info-score-badge my-info-score-badge--practice">Practice best {formatScore(practiceSat.total)}</span>
            </div>
          </div>
          {renderSatAttempts('Official SAT Attempts', 'satScores', satScores)}
          {renderSatAttempts('Practice SAT Attempts', 'practiceSatScores', practiceSatScores, true)}
        </div>

        <div className="my-info-test-family">
          <div className="my-info-test-family__header">
            <h3 className="my-info-subsection__title">ACT</h3>
            <div className="my-info-score-summary">
              <span className="my-info-score-badge">Official superscore {formatScore(officialAct.composite)}</span>
              <span className="my-info-score-badge my-info-score-badge--practice">Practice best {formatScore(practiceAct.composite)}</span>
            </div>
          </div>
          {renderActAttempts('Official ACT Attempts', 'actScores', actScores)}
          {renderActAttempts('Practice ACT Attempts', 'practiceActScores', practiceActScores, true)}
        </div>

        <div className="my-info-test-family">
          <div className="my-info-test-family__header">
            <h3 className="my-info-subsection__title">PSAT <InfoTooltip fieldName="psat" /></h3>
            <div className="my-info-score-summary">
              <span className="my-info-score-badge">Official best {formatScore(officialPsat)}</span>
              <span className="my-info-score-badge my-info-score-badge--practice">Practice best {formatScore(practicePsat)}</span>
            </div>
          </div>
          {renderPsatAttempts('Official PSAT Attempts', 'psatScores', psatScores)}
          {renderPsatAttempts('Practice PSAT Attempts', 'practicePsatScores', practicePsatScores, true)}
        </div>
      </section>
        </div>
      )}

      {activeSection === 'honors' && (
        <div className="my-info-tab-panel">
      <section className="my-info-section">
        <div className="my-info-section__header">
          <div>
            <h2 className="my-info-section__title">Extracurriculars & Awards</h2>
            <p className="my-info-section__subtitle">This setup is similar to what applications ask for, so you can organize roles, time commitments, descriptions, and awards before the real forms.</p>
          </div>
          <div className="my-info-section__count">{visibleActivities.length} activities · {visibleAwards.length} awards</div>
        </div>

        <div className="my-info-subsection">
          <div className="my-info-subsection__header">
            <h3 className="my-info-subsection__title">Extracurricular Activities</h3>
            <div className="my-info-section__count">{visibleActivities.length} / 10</div>
          </div>

          {extracurriculars.map((activity, index) => (
            <div key={index} className="my-info-activity-entry">
              <div className="my-info-grid my-info-grid--3col">
                <div className="my-info-field">
                  <label className="my-info-label">Activity Type</label>
                  <select className="my-info-activity-type-dropdown" value={activity.type || ''} onChange={(e) => handleExtracurricularChange(index, 'type', e.target.value)}>
                    <option value="">Select an activity type</option>
                    {ACTIVITY_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
                  </select>
                </div>
                <div className="my-info-field">
                  <label className="my-info-label">Position / Role</label>
                  <input className="my-info-input" type="text" value={activity.position || ''} onChange={(e) => handleExtracurricularChange(index, 'position', e.target.value)} placeholder="President, Coach" />
                </div>
                <div className="my-info-field">
                  <label className="my-info-label">Organization</label>
                  <input className="my-info-input" type="text" value={activity.organization || ''} onChange={(e) => handleExtracurricularChange(index, 'organization', e.target.value)} placeholder="Robotics Club" />
                </div>
              </div>

              <div className="my-info-field">
                <label className="my-info-label">Description (150 characters max)</label>
                <textarea
                  className="my-info-textarea my-info-textarea--sm"
                  value={activity.description || ''}
                  onChange={(e) => {
                    if (e.target.value.length <= 150) {
                      handleExtracurricularChange(index, 'description', e.target.value);
                    }
                  }}
                  placeholder="Brief description of your role and accomplishments"
                  rows={2}
                />
                <span className="my-info-char-count">{(activity.description || '').length} / 150</span>
              </div>

              <div className="my-info-grid my-info-grid--3col">
                <div className="my-info-field">
                  <label className="my-info-label">Hours per Week</label>
                  <input className="my-info-input" type="number" value={activity.hoursPerWeek || ''} onChange={(e) => handleExtracurricularChange(index, 'hoursPerWeek', e.target.value)} placeholder="10" min="0" />
                </div>
                <div className="my-info-field">
                  <label className="my-info-label">Weeks per Year</label>
                  <input className="my-info-input" type="number" value={activity.weeksPerYear || ''} onChange={(e) => handleExtracurricularChange(index, 'weeksPerYear', e.target.value)} placeholder="36" min="0" max="52" />
                </div>
                <div className="my-info-field">
                  <label className="my-info-label">Grade Levels</label>
                  <GradeCheckboxes selectedGrades={activity.grades || []} onChange={(grades) => handleExtracurricularGradesChange(index, grades)} />
                </div>
              </div>

              {extracurriculars.length > 1 && (
                <button className="my-info-remove-btn" onClick={() => handleRemoveExtracurricular(index)} type="button">Remove</button>
              )}
            </div>
          ))}

          {extracurriculars.length < 10 && (
            <button className="my-info-add-btn my-info-add-btn--full" onClick={handleAddExtracurricular} type="button">+ Add Activity</button>
          )}
        </div>

        <div className="my-info-subsection my-info-subsection--divided">
          <div className="my-info-subsection__header">
            <h3 className="my-info-subsection__title">Awards</h3>
            <div className="my-info-section__count">{visibleAwards.length} / 5</div>
          </div>

          {awards.map((award, index) => (
            <div key={index} className="my-info-activity-entry">
              <div className="my-info-grid my-info-grid--2col">
                <div className="my-info-field">
                  <label className="my-info-label">Award Title</label>
                  <input className="my-info-input" type="text" value={award.title || ''} onChange={(e) => handleAwardChange(index, 'title', e.target.value)} placeholder="Science Fair Winner" />
                </div>
                <div className="my-info-field">
                  <label className="my-info-label">Level</label>
                  <select className="my-info-input" value={award.level || 'school'} onChange={(e) => handleAwardChange(index, 'level', e.target.value)}>
                    <option value="school">School</option>
                    <option value="regional">Regional</option>
                    <option value="state">State</option>
                    <option value="national">National</option>
                    <option value="international">International</option>
                  </select>
                </div>
              </div>

              <div className="my-info-field">
                <label className="my-info-label">Grade Levels Received</label>
                <GradeCheckboxes selectedGrades={award.grades || []} onChange={(grades) => handleAwardGradesChange(index, grades)} />
              </div>

              {awards.length > 1 && (
                <button className="my-info-remove-btn" onClick={() => handleRemoveAward(index)} type="button">Remove</button>
              )}
            </div>
          ))}

          {awards.length < 5 && (
            <button className="my-info-add-btn my-info-add-btn--full" onClick={handleAddAward} type="button">+ Add Award</button>
          )}
        </div>
      </section>
        </div>
      )}

    </div>
  );
}

export default MyInfoPage;
